<template>
    <div :class="['tiptap-wrapper', { fullscreen: isFullscreen }]">
        <!-- Toolbar -->
        <div v-if="editor" class="editor-toolbar">
            <button
                @click="toggleBold"
                :class="{ active: editor.isActive('bold') }"
            >
                <b>B</b>
            </button>
            <button
                @click="toggleItalic"
                :class="{ active: editor.isActive('italic') }"
            >
                <i>I</i>
            </button>
            <button
                @click="toggleStrike"
                :class="{ active: editor.isActive('strike') }"
            >
                <s>S</s>
            </button>

            <button
                @click="toggleBlockquote"
                :class="{ active: editor.isActive('blockquote') }"
                title="Quote"
            >
                <font-awesome-icon icon="quote-right" />
            </button>

            <button
                @click="toggleBulletList"
                :class="{ active: editor.isActive('bulletList') }"
            >
                • List
            </button>

            <button
                @click="setTextAlign('left')"
                :class="{ active: editor.isActive({ textAlign: 'left' }) }"
                title="Align Left"
            >
                <font-awesome-icon icon="align-left" />
            </button>
            <button
                @click="setTextAlign('center')"
                :class="{ active: editor.isActive({ textAlign: 'center' }) }"
                title="Align Center"
            >
                <font-awesome-icon icon="align-center" />
            </button>
            <button
                @click="setTextAlign('right')"
                :class="{ active: editor.isActive({ textAlign: 'right' }) }"
                title="Align Right"
            >
                <font-awesome-icon icon="align-right" />
            </button>
            <button
                @click="setTextAlign('justify')"
                :class="{ active: editor.isActive({ textAlign: 'justify' }) }"
                title="Justify"
            >
                <font-awesome-icon icon="align-justify" />
            </button>

            <button @click="setHeading(1)">H1</button>
            <button @click="setHeading(2)">H2</button>
            <button @click="setHeading(3)">H3</button>

            <button @click="addImage">🖼️ Add Image</button>

            <button @click="toggleFullscreen" title="Toggle Fullscreen">
                <font-awesome-icon
                    :icon="isFullscreen ? 'compress' : 'expand'"
                />
            </button>
        </div>

        <!-- Editor -->
        <EditorContent v-if="editor" :editor="editor" class="editor-content" />
    </div>
</template>

<script>
import { defineComponent, h } from "vue";
import {
    Editor,
    EditorContent,
    VueNodeViewRenderer,
    NodeViewWrapper,
} from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { Image as TiptapImage } from "@tiptap/extension-image";
import TextAlign from "@tiptap/extension-text-align";
import { TextSelection } from "@tiptap/pm/state";
import { mergeAttributes } from "@tiptap/core";

/** ─────────────────────
 *  Resizable Image NodeView
 *  ───────────────────── */
const ResizableImageView = defineComponent({
    name: "ResizableImageView",
    components: { NodeViewWrapper },
    props: ["node", "updateAttributes", "selected", "editor", "getPos"],
    data() {
        return {
            isResizing: false,
            startX: 0,
            startWidth: 0,
        };
    },
    methods: {
        onMouseDown(e) {
            e.preventDefault();
            e.stopPropagation();
            this.beginResize(e.clientX);
            window.addEventListener("mousemove", this.onMouseMove);
            window.addEventListener("mouseup", this.onMouseUp, { once: true });
        },
        onMouseMove(e) {
            this.continueResize(e.clientX);
        },
        onMouseUp() {
            this.endResize();
            window.removeEventListener("mousemove", this.onMouseMove);
        },
        onTouchStart(e) {
            e.preventDefault();
            e.stopPropagation();
            const t = e.touches[0];
            this.beginResize(t.clientX);
            window.addEventListener("touchmove", this.onTouchMove, {
                passive: false,
            });
            window.addEventListener("touchend", this.onTouchEnd, {
                once: true,
            });
        },
        onTouchMove(e) {
            const t = e.touches[0];
            this.continueResize(t.clientX);
        },
        onTouchEnd() {
            this.endResize();
            window.removeEventListener("touchmove", this.onTouchMove);
        },

        beginResize(clientX) {
            this.isResizing = true;
            this.startX = clientX;
            const el = this.$refs.img;
            const current = this.node.attrs.width || Math.round(el.clientWidth);
            this.startWidth = current;
        },
        continueResize(clientX) {
            if (!this.isResizing) return;
            const dx = clientX - this.startX;
            const newWidth = Math.max(50, Math.round(this.startWidth + dx));
            this.updateAttributes({ width: newWidth });
        },
        endResize() {
            this.isResizing = false;
        },

        setAlignment(align) {
            this.updateAttributes({ textAlign: align });
        },
    },
    render() {
        const widthPx = this.node.attrs.width
            ? `${this.node.attrs.width}px`
            : undefined;
        const align = this.node.attrs.textAlign || "center";

        return h(
            NodeViewWrapper,
            {
                class: [
                    "image-node-wrap",
                    this.selected ? "selected" : "",
                    `align-${align}`,
                ],
                style: {
                    textAlign: align,
                    display: "block",
                    maxWidth: "100%",
                    lineHeight: "0",
                },
            },
            [
                h(
                    "div",
                    {
                        class: "image-inner-wrap",
                        style: {
                            display: "inline-block",
                            position: "relative",
                        },
                    },
                    [
                        h("img", {
                            ref: "img",
                            src: this.node.attrs.src,
                            class: "img-fluid resizable-image",
                            style: {
                                width: widthPx,
                                maxWidth: "100%",
                                height: "auto",
                                borderRadius: "4px",
                                display: "block",
                            },
                            draggable: false,
                        }),

                        // delete
                        h("button", {
                            class: "delete-image-btn",
                            innerHTML: "🗑️",
                            onClick: (e) => {
                                e.preventDefault();
                                this.editor.commands.deleteSelection();
                            },
                        }),

                        // resize handle
                        h("div", {
                            class: "resize-handle",
                            onMousedown: this.onMouseDown,
                            onTouchstart: this.onTouchStart,
                        }),

                        // alignment buttons
                        h(
                            "div",
                            { class: "image-align-controls" },
                            ["left", "center", "right"].map((pos) =>
                                h(
                                    "button",
                                    {
                                        class: [
                                            "align-btn",
                                            align === pos ? "active" : "",
                                        ],
                                        onClick: (e) => {
                                            e.preventDefault();
                                            this.setAlignment(pos);
                                        },
                                        title:
                                            pos === "left"
                                                ? "Align left"
                                                : pos === "right"
                                                ? "Align right"
                                                : "Align center",
                                    },
                                    pos === "left"
                                        ? "⬅️"
                                        : pos === "right"
                                        ? "➡️"
                                        : "⏺️"
                                )
                            )
                        ),
                    ]
                ),
            ]
        );
    },
});

/** ─────────────────────
 *  Image extension (attrs + NodeView)
 *  ───────────────────── */
/* replace the existing ResizableImage definition with this */
const ResizableImage = TiptapImage.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: null,
                parseHTML: (element) => {
                    const w = element.getAttribute("width");
                    return w ? Number(w) : null;
                },
                renderHTML: (attrs) =>
                    attrs.width ? { width: attrs.width } : {},
            },
            // emit data-align so we can wrap in a <figure> on serialization
            textAlign: {
                default: "center",
                parseHTML: (element) => element.style.textAlign || "center",
                renderHTML: (attrs) => {
                    return { "data-align": attrs.textAlign || "center" };
                },
            },
            class: {
                default: "img-fluid resizable-image",
                parseHTML: (element) =>
                    element.getAttribute("class") ||
                    "img-fluid resizable-image",
                renderHTML: (attrs) => ({
                    class: attrs.class || "img-fluid resizable-image",
                }),
            },
        };
    },

    renderHTML({ HTMLAttributes }) {
        // HTMLAttributes will include data-align if present
        const align = HTMLAttributes["data-align"] || "center";

        // Build img attributes - drop data-align so it doesn't appear on <img>
        const imgAttrs = { ...HTMLAttributes };
        delete imgAttrs["data-align"];

        // merge with any default HTMLAttributes specified on the extension
        const mergedImgAttrs = mergeAttributes(
            this.options.HTMLAttributes || {},
            imgAttrs
        );

        // return a figure wrapper with the same class names the editor's NodeView uses
        return [
            "figure",
            { class: `image-node-wrap align-${align}` },
            ["img", mergedImgAttrs],
        ];
    },

    addNodeView() {
        return VueNodeViewRenderer(ResizableImageView);
    },
});

export default {
    name: "TiptapEditor",
    components: { EditorContent },
    props: ["modelValue"],
    emits: ["update:modelValue"],
    data() {
        return {
            editor: null,
            isFullscreen: false,
        };
    },
    mounted() {
        this.editor = new Editor({
            extensions: [
                StarterKit,
                TextAlign.configure({ types: ["heading", "paragraph"] }),
                ResizableImage.configure({
                    inline: false,
                    allowBase64: true,
                    HTMLAttributes: { class: "img-fluid resizable-image" },
                }),
            ],
            content: this.modelValue,
            onUpdate: ({ editor }) => {
                this.$emit("update:modelValue", editor.getHTML());
            },
        });

        // Click blank area focuses near top
        this.$el.addEventListener("click", (e) => {
            const clickedWrapper =
                e.target.classList.contains("editor-content");
            if (!clickedWrapper) return;
            e.preventDefault();
            const view = this.editor?.view;
            if (!view) return;
            view.focus();

            const pmEl = this.$el.querySelector(".ProseMirror");
            const rect = pmEl.getBoundingClientRect();
            const coords = { left: rect.left + 4, top: rect.top + 4 };
            const posAtTop = view.posAtCoords(coords);
            const pos = posAtTop?.pos ?? 1;
            const tr = view.state.tr.setSelection(
                TextSelection.create(view.state.doc, pos)
            );
            view.dispatch(tr);
            pmEl.scrollTop = 0;
        });

        // ---------- Paste / Drop handlers (files, HTML, URLs) ----------
        const handleFiles = async (files) => {
            const file = Array.from(files || []).find((f) =>
                /^image\//.test(f.type)
            );
            if (!file) return;
            const base64 = await this.resizeAndConvertToBase64(
                file,
                1600,
                1600
            );
            this.editor
                .chain()
                .focus()
                .setImage({ src: base64, class: "img-fluid resizable-image" })
                .run();
        };

        const fetchUrlToBlob = async (url) => {
            try {
                const res = await fetch(url, { mode: "cors" });
                if (!res.ok) throw new Error("fetch failed");
                return await res.blob();
            } catch (e) {
                return null;
            }
        };

        const handleImageUrl = async (url) => {
            // try to fetch -> blob -> resize -> base64
            const blob = await fetchUrlToBlob(url);
            if (blob) {
                const base64 = await this.resizeAndConvertToBase64(
                    blob,
                    1600,
                    1600
                );
                this.editor
                    .chain()
                    .focus()
                    .setImage({
                        src: base64,
                        class: "img-fluid resizable-image",
                    })
                    .run();
            } else {
                // fallback: insert external URL (still responsive)
                this.editor
                    .chain()
                    .focus()
                    .setImage({ src: url, class: "img-fluid resizable-image" })
                    .run();
            }
        };

        const handlePastedHtml = async (html) => {
            const tmp = document.createElement("div");
            tmp.innerHTML = html;
            const img = tmp.querySelector("img");
            if (img?.src) {
                await handleImageUrl(img.src);
                return true;
            }
            return false;
        };

        const isImageUrlText = (text) =>
            /^https?:\/\/.+\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(
                text.trim()
            );

        // PASTE
        this.$el.addEventListener("paste", async (e) => {
            const files = e.clipboardData?.files;
            if (files && files.length) {
                e.preventDefault();
                await handleFiles(files);
                return;
            }

            const html = e.clipboardData?.getData("text/html");
            if (html && html.includes("<img")) {
                e.preventDefault();
                const handled = await handlePastedHtml(html);
                if (handled) return;
            }

            const text = e.clipboardData?.getData("text/plain");
            if (text && isImageUrlText(text)) {
                e.preventDefault();
                await handleImageUrl(text.trim());
            }
        });

        // DROP
        this.$el.addEventListener("dragover", (e) => {
            if (e.dataTransfer?.types?.includes("Files")) e.preventDefault();
        });
        this.$el.addEventListener("drop", async (e) => {
            const files = e.dataTransfer?.files;
            if (files && files.length) {
                e.preventDefault();
                await handleFiles(files);
                return;
            }
            const text = e.dataTransfer?.getData("text/plain");
            if (text && isImageUrlText(text)) {
                e.preventDefault();
                await handleImageUrl(text.trim());
            }
        });
    },
    beforeUnmount() {
        this.editor?.destroy();
    },
    methods: {
        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen;
        },
        toggleBold() {
            this.editor?.chain().focus().toggleBold().run();
        },
        toggleItalic() {
            this.editor?.chain().focus().toggleItalic().run();
        },
        toggleStrike() {
            this.editor?.chain().focus().toggleStrike().run();
        },
        toggleBulletList() {
            this.editor?.chain().focus().toggleBulletList().run();
        },
        setHeading(level) {
            this.editor?.chain().focus().toggleHeading({ level }).run();
        },
        toggleBlockquote() {
            this.editor?.chain().focus().toggleBlockquote().run();
        },

        async addImage() {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/*";
            input.onchange = async (event) => {
                const file = event.target.files?.[0];
                if (!file) return;
                const base64 = await this.resizeAndConvertToBase64(
                    file,
                    1600,
                    1600
                );
                if (base64) {
                    this.editor
                        .chain()
                        .focus()
                        .setImage({
                            src: base64,
                            class: "img-fluid resizable-image",
                        })
                        .run();
                }
            };
            input.click();
        },

        resizeAndConvertToBase64(fileOrBlob, maxWidth, maxHeight) {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const img = new window.Image();
                    img.onload = () => {
                        let { width, height } = img;
                        if (width > maxWidth || height > maxHeight) {
                            const ratio = Math.min(
                                maxWidth / width,
                                maxHeight / height
                            );
                            width = Math.round(width * ratio);
                            height = Math.round(height * ratio);
                        }
                        const canvas = document.createElement("canvas");
                        canvas.width = width;
                        canvas.height = height;
                        const ctx = canvas.getContext("2d");
                        ctx.drawImage(img, 0, 0, width, height);
                        resolve(canvas.toDataURL("image/jpeg", 0.85));
                    };
                    img.src = e.target.result;
                };
                reader.readAsDataURL(fileOrBlob);
            });
        },

        setTextAlign(alignment) {
            this.editor?.chain().focus().setTextAlign(alignment).run();
        },
    },
};
</script>

<style scoped>
.tiptap-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
}

/* Toolbar */
.editor-toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    background: #222;
    padding: 0.5rem;
    border-bottom: 1px solid #444;
}
.editor-toolbar button {
    background: #333;
    color: white;
    border: none;
    padding: 0.3rem 0.6rem;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.2s;
}
.editor-toolbar button.active,
.editor-toolbar button:hover {
    background: #555;
}

/* Editor content */
.editor-content {
    flex: 1;
    padding: 1rem;
    background: #111;
    color: white;
    border: 1px solid #444;
    border-radius: 0 0 4px 4px;
    overflow-y: auto;
    cursor: text;
}

/* Core editor area */
:deep(.ProseMirror) {
    min-height: 100%;
    outline: none;
    cursor: text;
}

/* NodeView wrapper for image + controls */
:deep(.image-node-wrap) {
    display: block;
    text-align: center;
    max-width: 100%;
    line-height: 0;
}
:deep(.image-node-wrap.align-left) {
    text-align: left;
}
:deep(.image-node-wrap.align-right) {
    text-align: right;
}

/* Ensure buttons are anchored to the image */
:deep(.image-inner-wrap) {
    position: relative;
    display: inline-block;
}

/* Highlight active image */
:deep(.image-node-wrap.selected img) {
    box-shadow: 0 0 10px rgba(0, 191, 255, 0.7);
    border-radius: 4px;
}

/* Hide controls by default */
:deep(.image-inner-wrap .resize-handle),
:deep(.image-inner-wrap .delete-image-btn),
:deep(.image-align-controls) {
    opacity: 0;
    pointer-events: none;
    transform: scale(0.8);
    transition: opacity 0.25s ease, transform 0.25s ease;
}

/* Show when selected */
:deep(.image-node-wrap.selected .resize-handle),
:deep(.image-node-wrap.selected .delete-image-btn),
:deep(.image-node-wrap.selected .image-align-controls) {
    opacity: 1;
    pointer-events: auto;
    transform: scale(1);
}

/* Resize handle — bottom-right, anchored to image */
:deep(.resize-handle) {
    position: absolute;
    bottom: -16px;
    right: -16px;
    width: 32px;
    height: 32px;
    background: rgba(0, 191, 255, 0.9);
    border: 2px solid #fff;
    border-radius: 50%;
    cursor: nwse-resize;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.6);
    z-index: 3;
    touch-action: none;
    transition: transform 0.2s ease, background 0.2s;
}
:deep(.resize-handle:active) {
    background: rgba(0, 191, 255, 1);
    transform: scale(0.9);
}

/* Delete button — bottom-left */
:deep(.delete-image-btn) {
    position: absolute;
    bottom: -16px;
    left: -16px;
    background: #ff4d4d;
    color: white;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    font-size: 18px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.6);
    z-index: 5;
    touch-action: manipulation;
    transition: background 0.2s, transform 0.25s ease;
}
:deep(.delete-image-btn:hover) {
    background: #ff1a1a;
    transform: scale(1.05);
}

/* Alignment controls — bottom center */
:deep(.image-align-controls) {
    position: absolute;
    bottom: -16px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 8px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.25s ease, transform 0.25s ease;
    z-index: 4;
}
:deep(.image-node-wrap.selected .image-align-controls) {
    opacity: 1;
    pointer-events: auto;
    transform: translateX(-50%) scale(1);
}

/* Buttons */
:deep(.image-align-controls .align-btn) {
    background: #333;
    color: white;
    border: 2px solid #fff;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 0 6px rgba(0, 0, 0, 0.6);
    transition: background 0.2s, transform 0.2s;
}
:deep(.image-align-controls .align-btn:hover) {
    background: #555;
    transform: scale(1.1);
}
:deep(.image-align-controls .align-btn.active) {
    background: rgba(0, 191, 255, 0.9);
    border-color: #fff;
}

/* Fullscreen */
.tiptap-wrapper.fullscreen {
    position: fixed !important;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: #111;
    z-index: 9999;
    border-radius: 0;
    display: flex;
    flex-direction: column;
}
.tiptap-wrapper.fullscreen .editor-content {
    flex: 1;
    height: 100%;
    overflow-y: auto;
}

/* Parchment quote styling (uses /public/parchment.jpg via absolute path) */
:deep(blockquote) {
    background: url("@/../public/parchment.jpg") center/cover repeat;
    padding: 1.5rem 2rem;
    margin: 1.5rem auto;
    max-width: 80%;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.25);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.3);
    color: #111;
    font-size: 1.1rem;
    font-family: "Georgia", serif;
    text-align: center;
    position: relative;
}
:deep(blockquote::before) {
    content: "“";
    font-size: 3rem;
    color: rgba(0, 0, 0, 0.4);
    position: absolute;
    top: 0.4rem;
    left: 0.8rem;
}
:deep(blockquote p) {
    margin: 0;
    font-size: 1.1rem;
    line-height: 1.5;
}
</style>

